import { useEffect } from 'react';
import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';

/**
 * Telemetry for images rendered via CSS (`background-image` / `mask-image`),
 * which cannot fire a JS `onError`. Loads a throwaway probe `Image()` for the
 * same URL purely to detect and report load failures to Sentry — the browser
 * serves both the CSS asset and the probe from one cached fetch, so the extra
 * request is effectively free. Dedup lives in captureMediaLoadError.
 *
 * @param url     the remote image URL, or null/undefined to skip probing.
 * @param context short label for where the image lives (e.g. 'phrase-icon').
 */
export function useCssImageErrorProbe(
  url: string | null | undefined,
  context: string
): void {
  useEffect(() => {
    if (!url) return undefined;

    const probe = new Image();
    const handleError = () => {
      captureMediaLoadError(new Error('Image failed to load'), {
        kind: 'image',
        src: url,
        context
      });
    };

    probe.addEventListener('error', handleError);
    probe.src = url;

    return () => {
      probe.removeEventListener('error', handleError);
      // Drop the in-flight probe request when the URL changes or unmounts.
      probe.src = '';
    };
  }, [url, context]);
}
